import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { NERC_FUNCTIONS, NERC_REGIONS } from "../lib/constants";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { ProfileHistory } from "./ProfileHistory";
import { Plus, Trash, FloppyDisk, Info, ShieldCheck, Prohibit, CheckCircle } from "@phosphor-icons/react";

export default function EntityProfile() {
  const [profile, setProfile] = useState(null);
  const [wizard, setWizard] = useState({ items: [] });
  const [wizardSaving, setWizardSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [histKey, setHistKey] = useState(0);

  const loadSummary = () => api.get("/profile/applicability-summary").then((r) => setSummary(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/entity-profile").then((r) => setProfile(r.data));
    api.get("/profile/wizard").then((r) => setWizard(r.data)).catch(() => {});
    loadSummary();
  }, []);

  if (!profile) return <div className="p-6 font-mono text-sm">Loading…</div>;

  const toggleFn = (code) => {
    const has = profile.registered_functions.includes(code);
    setProfile({ ...profile, registered_functions: has
      ? profile.registered_functions.filter((c) => c !== code)
      : [...profile.registered_functions, code] });
  };
  const toggleImpact = (lvl) => {
    const cur = profile.impact_ratings || [];
    const next = cur.includes(lvl) ? cur.filter((x) => x !== lvl) : [...cur, lvl];
    // Selecting any impact level clears the mutually-exclusive "No CIP assets" flag.
    setProfile({ ...profile, impact_ratings: next, no_cip_assets: next.length > 0 ? false : profile.no_cip_assets });
  };
  const toggleRegion = (code) => {
    const cur = profile.regional_entities || [];
    setProfile({ ...profile, regional_entities: cur.includes(code) ? cur.filter((x) => x !== code) : [...cur, code] });
  };
  const addFacility = () => setProfile({ ...profile, facilities: [...(profile.facilities || []), { id: crypto.randomUUID(), name: "", description: "" }] });
  const setFacility = (i, k, v) => {
    const facilities = [...profile.facilities];
    facilities[i] = { ...facilities[i], [k]: v };
    setProfile({ ...profile, facilities });
  };
  const removeFacility = (i) => setProfile({ ...profile, facilities: profile.facilities.filter((_, idx) => idx !== i) });

  const save = async () => {
    try {
      const { data } = await api.put("/entity-profile", {
        name: profile.name, acronym: profile.acronym || "", ncr_id: profile.ncr_id || "",
        registered_functions: profile.registered_functions,
        facilities: profile.facilities || [], impact_ratings: profile.impact_ratings || [],
        no_cip_assets: !!profile.no_cip_assets,
        regional_entities: profile.regional_entities || [],
        glossary_terms: profile.glossary_terms || [],
        compliance_period_start: profile.compliance_period_start || null,
        compliance_period_end: profile.compliance_period_end || null,
        annual_interpretation: profile.annual_interpretation || "calendar_year",
      });
      setProfile(data); setHistKey((k) => k + 1); toast.success("Entity profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="border border-border bg-card p-5">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[240px]">
            <Label>Registered Entity Name</Label>
            <Input data-testid="entity-name-input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="rounded-none mt-1" placeholder="e.g. Acme Power Cooperative" />
          </div>
          <div className="w-40">
            <Label>Acronym</Label>
            <Input data-testid="entity-acronym-input" value={profile.acronym || ""} onChange={(e) => setProfile({ ...profile, acronym: e.target.value })}
              className="rounded-none mt-1 font-mono" placeholder="e.g. APC" />
          </div>
          <div className="w-44">
            <Label>NCR ID</Label>
            <Input data-testid="entity-ncr-input" value={profile.ncr_id || ""} onChange={(e) => setProfile({ ...profile, ncr_id: e.target.value })}
              className="rounded-none mt-1 font-mono" placeholder="e.g. NCR12345" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Manuals introduce the full name with the acronym in parentheses on first use — e.g. &ldquo;{profile.name || "Acme Power Cooperative"}{profile.acronym ? ` (${profile.acronym})` : " (APC)"}&rdquo; — then use the acronym thereafter. The NCR ID appears on generated NERC self-report letters.</p>
      </div>

      <div className="border border-border bg-card p-5">
        <Label>Registered Functions</Label>
        <p className="text-xs text-muted-foreground mb-3">Determines which standards & requirement parts apply to you.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {NERC_FUNCTIONS.map(([code, label, flag]) => (
            <label key={code} className="flex items-center gap-2 text-sm cursor-pointer border border-border px-2 py-1.5 hover:bg-secondary">
              <Checkbox data-testid={`fn-${code}`} checked={profile.registered_functions.includes(code)} onCheckedChange={() => toggleFn(code)} className="rounded-none" />
              <span className="font-mono font-bold">{code}</span>
              <span className="text-muted-foreground text-xs truncate">{label}</span>
              {flag === "future" && <span data-testid={`fn-future-${code}`} className="ml-auto text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 bg-secondary border border-border text-muted-foreground shrink-0">Future</span>}
            </label>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card p-5">
        <Label>NERC Regional Entities</Label>
        <p className="text-xs text-muted-foreground mb-3">Select the Regional Entity(ies) your registration falls under. This brings region-specific standards (e.g. <span className="font-mono">BAL-001-TRE-2</span> for TRE) into your applicable standards. Leave all unchecked to see standards from every region.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {NERC_REGIONS.map(([code, label]) => (
            <label key={code} className="flex items-center gap-2 text-sm cursor-pointer border border-border px-2 py-1.5 hover:bg-secondary">
              <Checkbox data-testid={`region-${code}`} checked={(profile.regional_entities || []).includes(code)} onCheckedChange={() => toggleRegion(code)} className="rounded-none" />
              <span className="font-mono font-bold">{code}</span>
              <span className="text-muted-foreground text-xs truncate">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card p-5">
        <Label>CIP BES Cyber System Impact Ratings</Label>
        <p className="text-xs text-muted-foreground mb-3">Select the impact rating(s) the entity has identified per CIP-002. Drives CIP applicability (e.g., a Low-only entity only addresses CIP-002 &amp; CIP-003 Low-impact parts).</p>
        <div className="flex flex-wrap gap-2">
          {[["high", "High Impact"], ["medium", "Medium Impact"], ["low", "Low Impact"]].map(([lvl, label]) => (
            <label key={lvl} className="flex items-center gap-2 text-sm cursor-pointer border border-border px-3 py-2 hover:bg-secondary">
              <Checkbox data-testid={`impact-${lvl}`} checked={(profile.impact_ratings || []).includes(lvl)} onCheckedChange={() => toggleImpact(lvl)} className="rounded-none" />
              <span className="font-semibold">{label}</span>
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm cursor-pointer border border-dashed border-amber-400 bg-amber-50/50 dark:bg-amber-950/20 px-3 py-2 hover:bg-amber-100/50">
            <Checkbox
              data-testid="no-cip-assets"
              checked={!!profile.no_cip_assets}
              onCheckedChange={(v) => setProfile({
                ...profile,
                no_cip_assets: !!v,
                // If they check "no CIP assets", clear any impact ratings they had.
                impact_ratings: v ? [] : (profile.impact_ratings || []),
              })}
              className="rounded-none"
            />
            <span className="font-semibold text-amber-800 dark:text-amber-200">No NERC CIP Assets</span>
          </label>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
          <span className="font-semibold">&ldquo;No NERC CIP Assets&rdquo;</span> — check this if your entity owns/operates no NERC CIP-scoped BES Cyber Systems.
          {" "}Only CIP-002 will remain applicable (to affirm categorization). If your entity is registered <span className="font-mono">only</span> as GO2 and/or GOP2 (Category-2 generators), CIP standards do not apply at all — this is automatic regardless of whether &ldquo;No NERC CIP Assets&rdquo; is checked.
          {" "}Leave everything unchecked to keep all CIP standards visible until you complete CIP-002 categorization.
        </p>
      </div>

      <div className="border border-border bg-card p-5">
        <Label>Entity-Identified Glossary Terms (optional)</Label>
        <p className="text-xs text-muted-foreground mb-2">Capitalized terms your programs define, in addition to the NERC Glossary terms auto-detected in your policies. Comma-separated. Listed in the policy/manual Glossary with a reference to the NERC Glossary of Terms.</p>
        <Input data-testid="glossary-terms-input" value={(profile.glossary_terms || []).join(", ")} onChange={(e) => setProfile({ ...profile, glossary_terms: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} placeholder="e.g. Acme Control Network, Site Access Authority" className="rounded-none" />
      </div>

      <div className="border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-2">
          <Label>Facilities / Asset Profiles</Label>
          <Button data-testid="add-facility-btn" onClick={addFacility} variant="outline" size="sm" className="rounded-none h-7"><Plus size={14} className="mr-1" />Add</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Optional — capture asset groups used for applicability determinations.</p>
        {(profile.facilities || []).length === 0 && <p className="text-sm text-muted-foreground">No facilities added.</p>}
        <div className="space-y-2">
          {(profile.facilities || []).map((f, i) => (
            <div key={f.id || i} className="flex gap-2 items-start">
              <Input value={f.name} onChange={(e) => setFacility(i, "name", e.target.value)} placeholder="Facility name" className="rounded-none" />
              <Input value={f.description} onChange={(e) => setFacility(i, "description", e.target.value)} placeholder="Description" className="rounded-none" />
              <button onClick={() => removeFacility(i)} className="text-muted-foreground hover:text-red-600 mt-2"><Trash size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card p-5">
        <Label>How this entity treats “Annual” / “Calendar Year”</Label>
        <p className="text-xs text-muted-foreground mb-3">Per the ERO CMEP practice guide, when a standard says “annual” or “calendar year” without defining it, the entity must pick one consistent interpretation and apply it across the organization. This drives the <span className="font-semibold">Compliance date</span> on CIPWatch/OPWatch for annual obligations. (Standards that state an explicit interval — e.g. “15 calendar months”, “12 calendar years”, “60 calendar days” — always follow that exact wording.)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[["calendar_year", "Calendar Year", "Complete at least once each year (by Dec 31 of the following year)."],
            ["rolling_12_months", "Rolling 12 Months", "Complete within 12 months of the previous completion."]].map(([val, label, desc]) => (
            <label key={val} className={`flex items-start gap-2 text-sm cursor-pointer border px-3 py-2 ${(profile.annual_interpretation || "calendar_year") === val ? "border-primary bg-secondary" : "border-border hover:bg-secondary"}`}>
              <input type="radio" name="annual-interp" data-testid={`annual-interp-${val}`} className="mt-1 accent-indigo-600"
                checked={(profile.annual_interpretation || "calendar_year") === val}
                onChange={() => setProfile({ ...profile, annual_interpretation: val })} />
              <span><span className="font-semibold block">{label}</span><span className="text-xs text-muted-foreground">{desc}</span></span>
            </label>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card p-5">
        <Label>Current Compliance / Audit Period (optional)</Label>
        <p className="text-xs text-muted-foreground mb-3">Used by Missing Evidence to flag requirements whose attached evidence does not cover this date range.</p>
        <div className="flex flex-wrap gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Start</Label>
            <Input data-testid="compliance-period-start" type="date" value={profile.compliance_period_start || ""}
              onChange={(e) => setProfile({ ...profile, compliance_period_start: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">End</Label>
            <Input data-testid="compliance-period-end" type="date" value={profile.compliance_period_end || ""}
              onChange={(e) => setProfile({ ...profile, compliance_period_end: e.target.value })} className="rounded-none mt-1" />
          </div>
        </div>
      </div>

      <Button data-testid="save-profile-btn" onClick={save} className="rounded-none bg-primary hover:bg-primary/90"><FloppyDisk size={16} className="mr-1" />Save Entity Profile</Button>

      {/* Applicability Wizard — yes/no scoping questions that gate multiple standards at once.
          As of 2026-07 this section absorbs the former "Systems & Assets Owned" block. */}
      <div className="border border-border p-4 mt-6" data-testid="applicability-wizard">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-semibold">Applicability Wizard</h3>
            <p className="text-xs text-muted-foreground">Answer once &mdash; auto-scopes MOD, PRC, EOP, VAR, NUC and CIP applicability across the library, trackers, dashboard and exports.</p>
          </div>
          <Button
            data-testid="save-wizard-btn"
            disabled={wizardSaving}
            className="rounded-none"
            onClick={async () => {
              try {
                setWizardSaving(true);
                const payload = { answers: Object.fromEntries(wizard.items.filter((i) => i.answer !== null).map((i) => [i.key, i.answer])) };
                const { data } = await api.post("/profile/wizard", payload);
                toast.success(`Wizard saved — ${data.updated} updated, ${data.seeded} seeded.`);
                const w = await api.get("/profile/wizard");
                setWizard(w.data);
                loadSummary();
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              finally { setWizardSaving(false); }
            }}
          >
            <FloppyDisk size={14} className="mr-1" />{wizardSaving ? "Saving…" : "Save Wizard"}
          </Button>
        </div>
        <div className="space-y-3 mt-3">
          {(wizard.items || []).map((it, idx) => (
            <div key={it.key} className="border border-border p-3" data-testid={`wizard-item-${it.key}`}>
              <div className="flex items-start gap-3 justify-between flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{idx + 1}. {it.prompt}</div>
                  {it.help && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{it.help}</div>}
                  {(() => {
                    const regFuncs = wizard.registered_functions || [];
                    const gates = it.gates || [];
                    const matched = (it.bypass_functions || []).filter((f) => regFuncs.includes(f));
                    const asNA = it.gate_level === "requirement" ? "Marks N/A" : "Excludes";
                    if (it.info_only) {
                      return <div data-testid={`wizard-badge-${it.key}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 border border-slate-300 bg-slate-50 text-slate-500"><Info size={12} />Informational only — excludes nothing</div>;
                    }
                    if (gates.length === 0) return null;
                    if (it.answer === false) {
                      if (matched.length > 0) {
                        return <div data-testid={`wizard-badge-${it.key}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700"><ShieldCheck size={12} />Not excluded — you&apos;re registered as {matched.join(" / ")} ({gates.join(", ")} stays applicable)</div>;
                      }
                      return <div data-testid={`wizard-badge-${it.key}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-800"><Prohibit size={12} />{asNA} now: {gates.join(", ")}</div>;
                    }
                    if (it.answer === true) {
                      return <div data-testid={`wizard-badge-${it.key}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 border border-slate-200 bg-white text-slate-500"><CheckCircle size={12} />Included — nothing excluded</div>;
                    }
                    return <div data-testid={`wizard-badge-${it.key}`} className="mt-2 text-[11px] text-muted-foreground font-mono">Answering No would {it.gate_level === "requirement" ? "mark N/A" : "exclude"}: {gates.join(", ")}</div>;
                  })()}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    data-testid={`wizard-${it.key}-yes`}
                    variant={it.answer === true ? "default" : "outline"}
                    size="sm"
                    className="rounded-none"
                    onClick={() => setWizard({ ...wizard, items: wizard.items.map((x) => x.key === it.key ? { ...x, answer: true } : x) })}
                  >Yes</Button>
                  <Button
                    data-testid={`wizard-${it.key}-no`}
                    variant={it.answer === false ? "default" : "outline"}
                    size="sm"
                    className="rounded-none"
                    onClick={() => setWizard({ ...wizard, items: wizard.items.map((x) => x.key === it.key ? { ...x, answer: false } : x) })}
                  >No</Button>
                  <Button
                    data-testid={`wizard-${it.key}-clear`}
                    variant="ghost"
                    size="sm"
                    className="rounded-none text-xs"
                    onClick={() => setWizard({ ...wizard, items: wizard.items.map((x) => x.key === it.key ? { ...x, answer: null } : x) })}
                  >Clear</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {summary && (
        <div className="border border-border" data-testid="applicability-summary">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
            <div>
              <h3 className="font-semibold flex items-center gap-2"><Prohibit size={16} />Applicability Summary</h3>
              <p className="text-xs text-muted-foreground">What&apos;s currently marked Not Applicable by your wizard answers, and why.</p>
            </div>
            <div className="text-right shrink-0" data-testid="summary-counts">
              <div className="text-2xl font-bold tabular-nums">{summary.total_standards}</div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">standards N/A{summary.total_requirements ? ` · ${summary.total_requirements} req N/A` : ""}</div>
            </div>
          </div>
          <div className="p-4">
            {(summary.causes || []).length === 0 ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2" data-testid="summary-empty">
                <CheckCircle size={16} className="text-emerald-600" />
                No standards are excluded by your wizard answers — everything is scoped by function and impact only.
              </div>
            ) : (
              <div className="space-y-3">
                {summary.causes.map((c) => (
                  <div key={c.key} className="border-l-2 border-amber-400 pl-3 py-1" data-testid={`summary-cause-${c.key}`}>
                    <div className="text-xs text-muted-foreground">Because you answered <b className="text-amber-700">No</b> to:</div>
                    <div className="text-sm font-medium">{c.prompt}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {c.excluded_standards.map((s) => (
                        <span key={s} className="text-[11px] font-mono px-1.5 py-0.5 border border-slate-300 bg-slate-50 text-slate-600">{s}</span>
                      ))}
                      {c.excluded_requirements.map((r) => (
                        <span key={r} className="text-[11px] font-mono px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800">{r} · N/A</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mt-6"><ProfileHistory refreshKey={histKey} onRestored={(p) => { setProfile(p); loadSummary(); }} /></div>
    </div>
  );
}
